import { 
  Newspaper,
  Brain,
  Video,
  Users,
  Bell,
  BarChart3,
  Shield,
  Smartphone,
  Globe,
  Zap,
  Eye,
  MessageSquare,
  FileText,
  Radio,
  Camera,
  Mic,
  TrendingUp,
  Lock,
  Database,
  Network,
  CheckCircle,
  Clock,
  Target,
  Activity,
  Rocket,
  Edit3,
  Share2,
  Search,
  Languages,
  Award,
  Layers,
  Rss,
  Bookmark,
  Image,
  Hash,
  Megaphone,
  PenTool,
  Sparkles,
  Headphones,
  PlayCircle,
  LayoutDashboard,
  Workflow
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const digitalNewsroomPlatformConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Brain,
      title: "AI-Powered Story Editor with Smart Suggestions",
      description: "Intelligent writing assistant with real-time headline suggestions, grammar checking, SEO optimization, and content recommendations powered by advanced AI.",
      metrics: "95% accuracy, 40% faster writing",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Video,
      title: "Multi-Channel Publishing & Video Production",
      description: "Publish to web, mobile apps, social media, and newsletters from one platform with integrated video encoding, transcription, and live streaming.",
      metrics: "10+ channels, real-time publishing",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: Users,
      title: "Collaborative Editorial Workflows",
      description: "Multi-stage review processes with deadline tracking, task assignment, and real-time collaboration between reporters, editors, and publishers.",
      metrics: "Unlimited workflow stages",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Bell,
      title: "Breaking News Alert System",
      description: "Instant push notifications and priority publishing for urgent stories with audience segmentation and real-time delivery tracking.",
      metrics: "Sub-second delivery, 98% reach",
      color: "from-red-500 to-orange-600"
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics & Monetization",
      description: "Comprehensive traffic analytics, subscription management, advertising integration, and revenue attribution with A/B testing capabilities.",
      metrics: "Real-time insights, ROI tracking",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Globe,
      title: "Multi-Language & Global Publishing",
      description: "AI-powered translation support, multi-language content management, and global CDN distribution for worldwide audience reach.",
      metrics: "50+ languages, global reach",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: Search,
      title: "Advanced Content Discovery & SEO",
      description: "AI-driven SEO optimization, keyword research tools, content scoring, and search engine submission with real-time ranking insights.",
      metrics: "300% organic traffic boost",
      color: "from-teal-500 to-cyan-600"
    },
    {
      icon: FileText,
      title: "Intelligent Content Library & DAM",
      description: "Centralized digital asset management with AI-powered tagging, version control, rights management, and instant content retrieval.",
      metrics: "10M+ assets supported",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Rss,
      title: "RSS & News Wire Integration",
      description: "Automated ingestion from news wires, RSS feeds, and partner outlets with intelligent deduplication and source attribution.",
      metrics: "500+ wire services supported",
      color: "from-rose-500 to-red-600"
    },
    {
      icon: Bookmark,
      title: "Reader Engagement & Subscriptions",
      description: "Complete subscription management, paywall configuration, member-only content, and engagement tracking for audience monetization.",
      metrics: "40% subscription conversion lift",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Hash,
      title: "Trending Topics & Hashtag Intelligence",
      description: "Real-time trending topic detection, viral content identification, and hashtag performance analytics across social platforms.",
      metrics: "Real-time trend detection",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: MessageSquare,
      title: "Audience Comments & Moderation",
      description: "AI-powered comment moderation, community engagement tools, spam filtering, and sentiment analysis for reader interactions.",
      metrics: "99% spam detection accuracy",
      color: "from-sky-500 to-blue-600"
    }
  ],
  
  advancedCapabilities: [
    {
      icon: Zap,
      title: "AI Content Intelligence & Automation",
      description: "Automated tagging, sentiment analysis, trend detection, and content recommendations powered by machine learning algorithms.",
      metrics: "90% automation accuracy",
      color: "from-yellow-500 to-orange-600"
    },
    {
      icon: Shield,
      title: "Secure Source Protection System",
      description: "End-to-end encrypted document drops, whistleblower protection, and secure communication channels for sensitive journalism.",
      metrics: "Military-grade encryption",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Smartphone,
      title: "Personalized Reader Experience",
      description: "ML-powered news feeds, personalized content delivery, offline reading mode, and intelligent push notification management.",
      metrics: "50% higher engagement",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Radio,
      title: "Live Streaming Production Suite",
      description: "Integrated live video broadcasting with RTMP/HLS support, real-time engagement tools, and automated recording and archiving.",
      metrics: "Ultra-low latency streaming",
      color: "from-red-500 to-pink-600"
    },
    {
      icon: Languages,
      title: "AI Translation & Localization",
      description: "Automated content translation with neural machine translation, localized publishing workflows, and multi-region content management.",
      metrics: "95% translation accuracy",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Share2,
      title: "Automated Social Distribution",
      description: "AI-scheduled posting across all social platforms with optimal timing, audience targeting, and engagement analytics.",
      metrics: "10x social reach improvement",
      color: "from-indigo-500 to-violet-600"
    },
    {
      icon: Mic,
      title: "Podcast & Audio Content Studio",
      description: "Full podcast production suite with AI transcription, audio editing, distribution to major platforms, and listener analytics.",
      metrics: "20+ podcast platforms",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: Image,
      title: "AI Image Generation & Editing",
      description: "Generate editorial images with AI, auto-crop for different formats, background removal, and smart image enhancement tools.",
      metrics: "1000s of images per day",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: PlayCircle,
      title: "Video Clipping & Highlights",
      description: "AI-powered video analysis to identify key moments, auto-generate highlight reels, and create social-ready clips from long-form content.",
      metrics: "10x faster video editing",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: Sparkles,
      title: "AI Headline & Caption Generator",
      description: "Generate compelling headlines, social captions, and meta descriptions optimized for engagement and SEO using advanced language models.",
      metrics: "3x higher click-through rates",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: LayoutDashboard,
      title: "Custom Dashboard Builder",
      description: "Create personalized editorial dashboards with drag-and-drop widgets, custom KPIs, and role-based views for different team members.",
      metrics: "Unlimited customization",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Workflow,
      title: "Workflow Automation Engine",
      description: "Build custom automation rules for content routing, approval chains, scheduled publishing, and cross-platform syndication.",
      metrics: "500+ automation triggers",
      color: "from-teal-500 to-green-600"
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'SOC 2 & ISO 27001 Compliance',
      description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and industry-specific media regulations',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Lock,
      title: 'Source Protection & Encryption',
      description: 'End-to-end encryption for all communications, secure document drops, and whistleblower protection systems',
      metrics: 'Military-grade security',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Cloud Infrastructure',
      description: 'Enterprise-grade cloud platform with auto-scaling, global CDN, and 99.99% uptime SLA for high-traffic events',
      metrics: '99.99% uptime guaranteed',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'DDoS Protection & CDN',
      description: 'Advanced DDoS mitigation with global content delivery network for fast, secure content distribution worldwide',
      metrics: '100% attack mitigation',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Eye,
      title: 'Multi-Factor Authentication',
      description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based access controls',
      metrics: 'Zero-trust access control',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'Comprehensive Audit Trails',
      description: 'Complete logging of all user actions, content changes, and system events for compliance and security monitoring',
      metrics: 'Complete audit visibility',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Database,
      title: 'Data Backup & Recovery',
      description: 'Automated hourly backups with point-in-time recovery, geo-redundant storage, and disaster recovery procedures',
      metrics: 'Zero data loss guarantee',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Network,
      title: 'Network Security Monitoring',
      description: 'Real-time network traffic analysis, intrusion detection, and automated threat response systems',
      metrics: 'Real-time threat detection',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Assessment',
      description: 'Regular penetration testing, vulnerability scanning, and security audits with automated remediation',
      metrics: 'Proactive security validation',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
